/* eslint-disable @next/next/no-img-element */
/**
 * ./src/modules/product/components/collections/CollectionSample.tsx
 *
 * CollectionSample component to display a Collection Sample.
 *
 * @copyright DesignShop, 2024
 * @since 05/10/2024
 */
import React from 'react';

import Link from 'next/link';

import { HitType } from '@/modules/common/lib/abtastyStub';

import Image from '@/modules/common/components/Image';
import { Match } from '@/modules/common/components/icons';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import { cn } from '@/modules/common/lib/utils';

interface CollectionSampleProps {
  name?: string;
  image?: string;
  color?: string;
  type?: string;
  size?: number;
  exactMatch?: boolean;
  href?: string;
  roundingSize?: 's' | 'm';
  productCategory?: string;
  className?: string;
}
export const CollectionSample = ({
  name,
  image,
  exactMatch,
  type,
  color,
  href,
  productCategory,
  size = 96,
  roundingSize = 'm',
  className,
}: CollectionSampleProps) => {
  const { sendFeatureFlagTracking } = useFeatureFlag();

  const content = (
    <div className="relative aspect-square w-full" data-testid="collection-sample">
      {exactMatch && (
        <div
          className={`absolute left-[-8px] top-[-8px] z-1 flex items-center justify-center rounded-3xl bg-white py-[2.5px] pl-1 pr-2 ${className}`}
          data-testid="exact-match-badge"
        >
          <Match />
          <span
            className="whitespace-nowrap leading-4 lg:text-[11px]"
            data-testid="exact-match-text"
          >
            Exact Match
          </span>
        </div>
      )}
      {type === 'paint' || !image ?
        <div
          className={cn(
            'h-full w-full rounded-[0.8rem] bg-gray-100',
            {
              'rounded-sm': roundingSize === 's',
            },
            className
          )}
          style={{ backgroundColor: color, boxShadow: '0px 0px 1px 0px #00000066 inset' }}
          data-testid="paint-sample"
        />
      : <Image
          className={cn(
            'block h-full w-full rounded-[0.8rem] object-cover',
            {
              'rounded-sm': roundingSize === 's',
            },
            className
          )}
          src={image}
          width={size || 84}
          height={size || 84}
          style={{
            boxShadow: '0px 0px 1px 0px #00000066 inset',
          }}
          alt={name || 'Sample image'}
          data-testid="sample-image"
        />
      }
    </div>
  );

  if (href) {
    return (
      <Link
        href={href}
        onClick={() => {
          sendFeatureFlagTracking({
            type: HitType.EVENT,
            category: 'User Engagement',
            action: 'Product Card Clicked',
            label: productCategory || '',
          });
        }}
      >
        {content}
      </Link>
    );
  } else {
    return content;
  }
};
